/**
 * Core API URL Helper
 *
 * Derives the Core API base URL from VITE_CORE_API_URL.
 * In staging/production, this is set to e.g. 'https://api.intrum.dev/core'.
 * The gateway strips '/core' prefix, so '/core/api/v1' becomes '/api/v1' when forwarded.
 */

import { requireBoundCell } from './global/cell-store';
import { isGlobalSurface } from './surface-mode';

/**
 * Chromium maps `*.localhost` to 127.0.0.1 (RFC 6761), ignoring Docker DNS.
 * Hub on `hub.test` (Playwright-in-Docker) therefore cannot reach Caddy via
 * `https://api.localhost/core`. Same-origin `/api` is already proxied on hub.test.
 */
function localhostApiUnreachableFromPage(configured: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const apiHost = new URL(configured).hostname;
    return window.location.hostname.endsWith('.test') && apiHost.endsWith('.localhost');
  } catch {
    return false;
  }
}

/**
 * Get the Core API base URL (without /api/v1 suffix)
 * Returns: 'https://api.intrum.dev/core' or '/api' for local dev
 */
export function getCoreApiBaseUrl(): string {
  if (isGlobalSurface()) return requireBoundCell().origins.core_api;
  const configured = import.meta.env.VITE_CORE_API_URL;
  if (configured && !localhostApiUnreachableFromPage(configured)) {
    return configured;
  }

  return '/api';
}

/**
 * Get the Core API base URL with /api/v1 suffix
 * Returns: 'https://api.intrum.dev/core/api/v1' or '/api/v1' for local dev
 *
 * Note: Core API routes are mounted at /api/v1, and the gateway strips /core prefix.
 * So '/core/api/v1/ledger/transfers' → gateway strips /core → '/api/v1/ledger/transfers' → core-api
 */
export function getCoreApiV1Url(): string {
  if (isGlobalSurface()) return `${requireBoundCell().origins.core_api}/api/v1`;
  const configured = import.meta.env.VITE_CORE_API_URL;
  if (configured && !localhostApiUnreachableFromPage(configured)) {
    return `${configured}/api/v1`;
  }

  return '/api/v1';
}
